import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  closeChatWindow,
  openChatWindow,
  selectFrontChatWindowOpen,
  setUnreadCount,
} from '../store';
import type { FrontChat } from '../types';

/**
 * Hook to handle Front Chat event listeners and sync with Redux
 */
export const useFrontChatEvents = (frontChat: FrontChat | undefined, isInitialized: boolean) => {
  const dispatch = useDispatch();
  const windowOpen = useSelector(selectFrontChatWindowOpen);

  // Sync Redux windowOpen state with FrontChat visibility
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    if (windowOpen) {
      frontChat('show');
    } else {
      frontChat('hide');
    }
  }, [frontChat, isInitialized, windowOpen]);

  // Listen for user-driven window visibility changes
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    let active = true;
    const handler = ({ is_window_visible }: { is_window_visible: boolean }) => {
      if (!active) return;
      if (is_window_visible) {
        dispatch(openChatWindow());
      } else {
        dispatch(closeChatWindow());
      }
    };

    frontChat('onWindowVisibilityChanged', handler);

    // Front has no unsubscribe API; ignore callbacks after switching support implementations.
    return () => {
      active = false;
    };
  }, [frontChat, isInitialized, dispatch]);

  // Listen for unread count changes
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    let active = true;
    const unreadHandler = ({ unread_count }: { unread_count: number }) => {
      if (active) dispatch(setUnreadCount(unread_count));
    };

    frontChat('onUnreadChange', unreadHandler);

    return () => {
      active = false;
    };
  }, [frontChat, isInitialized, dispatch]);
};
